import { useEffect, useState } from "react";
import { Loader2, Minus, Package, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Field, Thumb } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { ImageUpload } from "@/components/ds/ImageUpload";

/** Composição visual do kit: imagem própria tem prioridade, senão junta as fotos dos componentes. */
export function KitVisual({ photoPath, components, className = "h-16 w-16" }) {
  const photos = (components || []).map((c) => c.photo_path).filter(Boolean).slice(0, 4);
  if (photoPath) return <Thumb src={fileUrl(photoPath)} alt="Kit" className={className} />;
  if (photos.length === 0)
    return <Thumb src={null} alt="Kit" className={className} fallback={<Package className="h-5 w-5" />} />;
  return (
    <div className={`grid shrink-0 grid-cols-2 gap-0.5 overflow-hidden rounded-md border border-white/10 ${className}`}>
      {photos.map((p, i) => (
        <img
          key={p}
          src={fileUrl(p)}
          alt=""
          className={`h-full w-full object-cover ${photos.length === 1 ? "col-span-2 row-span-2" : ""} ${
            photos.length === 3 && i === 0 ? "col-span-2" : ""
          }`}
        />
      ))}
    </div>
  );
}

export function KitModal({ kit, products, categories = [], creators = [], onClose, onSaved }) {
  const [form, setForm] = useState(
    kit
      ? {
          name: kit.name,
          description: kit.description || "",
          price: String(kit.price ?? ""),
          consign_price: kit.consign_price ? String(kit.consign_price) : "",
          reseller_price: kit.reseller_price ? String(kit.reseller_price) : "",
          photo_path: kit.photo_path || null,
          active: kit.active !== false,
          show_in_catalog: kit.show_in_catalog !== false,
          featured: kit.featured === true,
        }      : {
          name: "",
          description: "",
          price: "",
          consign_price: "",
          reseller_price: "",
          photo_path: null,
          active: true,
          show_in_catalog: true,
          featured: false,
        }
  );
  const [items, setItems] = useState(
    (kit?.components || []).map((c) => ({ product_id: c.product_id, quantity: c.quantity }))
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [vars, setVars] = useState(
    (kit?.variations || []).map((v) => ({
      id: v.id,
      name: v.name,
      sku: v.sku || "",
      photo_path: v.photo_path || null,
      active: v.active !== false,
      show_in_catalog: v.show_in_catalog !== false,
    }))
  );
  const [busy, setBusy] = useState(false);
  const [catId, setCatId] = useState(kit?.category_id || "");
  const [creatorId, setCreatorId] = useState(kit?.creator_id || "");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const eligible = products.filter((p) => p.kit_eligible !== false);
  const byId = Object.fromEntries(products.map((p) => [p.id, p]));

  const add = (product) => {
    if (items.some((i) => i.product_id === product.id)) {
      toast.error("Este produto já está no kit.");
      return;
    }
    setItems((prev) => [...prev, { product_id: product.id, quantity: 1 }]);
    setPickerOpen(false);
  };

  const cost = items.reduce(
    (s, i) => s + Number(byId[i.product_id]?.cost || 0) * Number(i.quantity || 0),
    0
  );
  const sumIndividual = items.reduce(
    (s, i) => s + Number(byId[i.product_id]?.price || 0) * Number(i.quantity || 0),
    0
  );
  const price = Number(form.price || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length < 2) {
      toast.error("Um kit precisa de pelo menos 2 produtos.");
      return;
    }
    if (vars.some((v) => !v.name.trim())) {
      toast.error("Dê um nome para cada combinação do kit.");
      return;
    }
    setBusy(true);
    const body = {
      name: form.name,
      description: form.description,
      category_id: catId || null,
      creator_id: creatorId || null,
      price: Number(form.price || 0),
      consign_price: Number(form.consign_price || 0),
      reseller_price: Number(form.reseller_price || 0),
      photo_path: form.photo_path || null,
      active: !!form.active,
      show_in_catalog: !!form.show_in_catalog,
      featured: !!form.featured,
      variations: vars.map((v) => ({
        id: v.id || null,
        name: v.name.trim(),
        photo_path: v.photo_path || null,
        active: v.active !== false,
        show_in_catalog: v.show_in_catalog !== false,
      })),
      items: items.map((i) => ({ product_id: i.product_id, quantity: Number(i.quantity || 1) })),
    };
    try {
      if (kit) await api.put(`/kits/${kit.id}`, body);
      else await api.post("/kits", body);
      toast.success(kit ? "Kit atualizado com sucesso." : "Kit cadastrado com sucesso.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <ResponsiveModal
        open
        onOpenChange={(o) => !o && onClose()}
        title={kit ? "Editar Kit" : "Novo Kit"}
        description="O kit reúne produtos que já existem — nada é duplicado e o estoque continua sendo dos componentes."
        testid="kit-modal"
      >
        <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
          <ImageUpload
            value={form.photo_path}
            onChange={(p) => setForm((f) => ({ ...f, photo_path: p }))}
            testid="kit-image-upload"
          />
          <p className="-mt-2 text-xs text-muted-foreground">
            Sem foto própria, o catálogo monta a imagem juntando as fotos dos produtos do kit.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome do kit">
              <Input value={form.name} onChange={set("name")} required placeholder="Ex.: Kit Copão Monster" data-testid="kit-name-input" />
            </Field>
            <Field
              label="Categoria comercial"
              hint="Sem categoria, o kit continua aparecendo na vitrine Kits."
            >
              <NativeSelect
                value={catId}
                onChange={setCatId}
                placeholder="Kits (padrão)"
                data-testid="kit-category-select"
                options={categories
                  .filter((c) => c.active !== false)
                  .map((c) => ({ value: c.id, label: c.name }))}
              />
            </Field>
            <Field
              label="Criadora responsável"
              hint="Só com vínculo o kit aparece no catálogo dessa criadora."
              className="sm:col-span-2"
            >
              <NativeSelect
                value={creatorId}
                onChange={setCreatorId}
                placeholder="Sem criadora"
                data-testid="kit-creator-select"
                options={creators
                  .filter((c) => c.active !== false)
                  .map((c) => ({ value: c.id, label: c.display_name }))}
              />
            </Field>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Preços comerciais do kit
            </span>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Preço público (R$)">
                <Input type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className="font-num" data-testid="kit-price-input" />
              </Field>
              <Field label="Preço consignado (R$)">
                <Input type="number" min="0" step="0.01" value={form.consign_price} onChange={set("consign_price")} placeholder="não definido" className="font-num" data-testid="kit-consign-price-input" />
              </Field>
              <Field label="Preço lojista (R$)">
                <Input type="number" min="0" step="0.01" value={form.reseller_price} onChange={set("reseller_price")} placeholder="não definido" className="font-num" data-testid="kit-reseller-price-input" />
              </Field>
            </div>
            <p className="text-[11px] text-muted-foreground">
              O kit tem preço próprio: sem preço consignado definido, o parceiro não consegue pedir este kit em reposição.
            </p>
          </div>

          <Field label="Descrição">
            <Textarea value={form.description} onChange={set("description")} rows={2} data-testid="kit-description-input" />
          </Field>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Produtos do kit
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
                data-testid="add-kit-component-btn"
                className="h-8 gap-1.5 rounded-full text-xs"
              >
                <Plus className="h-3.5 w-3.5" /> Adicionar componente
              </Button>
            </div>

            {items.length === 0 ? (
              <p className="rounded-lg border border-dashed border-white/15 px-4 py-6 text-center text-sm text-muted-foreground" data-testid="kit-items-empty">
                Nenhum produto selecionado. Um kit precisa de pelo menos 2.
              </p>
            ) : (
              <ul className="space-y-2">
                {items.map((it, idx) => {
                  const product = byId[it.product_id];
                  return (
                    <li
                      key={it.product_id}
                      className="flex items-center gap-3 rounded-lg border border-white/10 bg-secondary/40 p-2.5"
                      data-testid={`kit-item-${it.product_id}`}
                    >
                      <Thumb src={fileUrl(product?.photo_path)} alt={product?.name} className="h-9 w-9" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm">{product?.name || "Produto removido"}</p>
                        <p className="font-num text-xs text-muted-foreground">
                          {brl(product?.price)} · custo {brl(product?.cost)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 rounded-full border border-white/10 bg-background/60 px-1.5 py-1">
                        <button
                          type="button"
                          onClick={() =>
                            setItems((prev) =>
                              prev.map((p, i) =>
                                i === idx ? { ...p, quantity: Math.max(1, p.quantity - 1) } : p
                              )
                            )
                          }
                          data-testid={`kit-item-dec-${it.product_id}`}
                          className="p-1 text-muted-foreground"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center font-num text-sm" data-testid={`kit-item-qty-${it.product_id}`}>
                          {it.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setItems((prev) =>
                              prev.map((p, i) => (i === idx ? { ...p, quantity: p.quantity + 1 } : p))
                            )
                          }
                          data-testid={`kit-item-inc-${it.product_id}`}
                          className="p-1 text-brand"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                        data-testid={`kit-item-remove-${it.product_id}`}
                        className="text-muted-foreground transition-colors hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="space-y-1.5 rounded-lg border border-white/10 bg-secondary/40 p-4 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Custo dos componentes</span>
              <span className="font-num" data-testid="kit-cost-preview">
                {brl(cost)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Somando os preços individuais</span>
              <span className="font-num text-xs text-muted-foreground">{brl(sumIndividual)}</span>
            </div>
            <div className="flex justify-between border-t border-white/10 pt-1.5">
              <span className="text-muted-foreground">Margem do kit</span>
              <span className="font-num text-brand" data-testid="kit-margin-preview">
                {brl(price - cost)}
              </span>
            </div>
          </div>

          <div className="space-y-3 rounded-lg border border-white/10 bg-secondary/40 p-4" data-testid="kit-variations-section">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Cores / combinações do kit
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Opção comercial do kit (ex.: "Rosa + Branco"). SKU gerado pelo sistema. O preço
                continua sendo do kit e a composição de produtos é sempre a mesma.
              </p>
            </div>
            {vars.map((v, idx) => (
              <div
                key={v.id || `new-${idx}`}
                className="space-y-3 rounded-md border border-white/10 bg-card/60 p-3"
                data-testid={`kit-variation-row-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <ImageUpload
                    value={v.photo_path}
                    onChange={(p) =>
                      setVars((prev) => prev.map((x, i) => (i === idx ? { ...x, photo_path: p } : x)))
                    }
                    testid={`kit-variation-image-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => setVars((prev) => prev.filter((_, i) => i !== idx))}
                    data-testid={`kit-variation-remove-${idx}`}
                    className="ml-auto text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Nome da combinação">
                    <Input
                      value={v.name}
                      onChange={(e) =>
                        setVars((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x))
                        )
                      }
                      placeholder="Ex.: Rosa + Branco"
                      data-testid={`kit-variation-name-${idx}`}
                    />
                  </Field>
                  <Field label="SKU" hint="Gerado automaticamente ao salvar.">
                    <Input
                      value={v.sku}
                      readOnly
                      disabled
                      placeholder="Gerado ao salvar"
                      className="font-num"
                      data-testid={`kit-variation-sku-${idx}`}
                    />
                  </Field>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-xs">
                    <Switch
                      checked={v.active !== false}
                      onCheckedChange={(value) =>
                        setVars((prev) => prev.map((x, i) => (i === idx ? { ...x, active: value } : x)))
                      }
                      data-testid={`kit-variation-active-${idx}`}
                    />
                    Ativa
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <Switch
                      checked={v.show_in_catalog !== false}
                      onCheckedChange={(value) =>
                        setVars((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, show_in_catalog: value } : x))
                        )
                      }
                      data-testid={`kit-variation-catalog-${idx}`}
                    />
                    No catálogo
                  </label>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="soft"
              onClick={() =>
                setVars((prev) => [
                  ...prev,
                  { id: null, name: "", sku: "", photo_path: null, active: true, show_in_catalog: true },
                ])
              }
              data-testid="kit-add-variation-btn"
              className="h-9 rounded-full px-4 text-xs"
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Adicionar combinação
            </Button>
          </div>

          <div className="space-y-3 rounded-lg border border-white/10 bg-secondary/40 p-4">
            <label className="flex items-center justify-between gap-3">
              <span className="text-sm">Exibir este kit no catálogo</span>
              <Switch
                checked={!!form.show_in_catalog}
                onCheckedChange={(v) => setForm((f) => ({ ...f, show_in_catalog: v }))}
                data-testid="kit-show-in-catalog-switch"
              />
            </label>
            <label className="flex items-center justify-between gap-3">
              <span className="text-sm">Destaque no catálogo</span>
              <Switch
                checked={!!form.featured}
                onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))}
                data-testid="kit-featured-switch"
              />
            </label>
            <label className="flex items-center justify-between gap-3">
              <span className="text-sm">Kit ativo</span>
              <Switch
                checked={!!form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
                data-testid="kit-active-switch"
              />
            </label>
          </div>

          <Button type="submit" disabled={busy} data-testid="save-kit-btn" className="h-11 rounded-full font-semibold transition-transform active:scale-95">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
          </Button>
        </form>
      </ResponsiveModal>

      <ResponsiveModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title="Selecionar produto"
        description="Somente produtos liberados para uso em kits."
        testid="kit-product-picker-modal"
      >
        <div className="mt-2 max-h-72 space-y-2 overflow-y-auto pr-1">
          {eligible.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nenhum produto liberado para kits.
            </p>
          )}
          {eligible.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => add(p)}
              data-testid={`kit-picker-option-${p.id}`}
              className="flex w-full items-center gap-3 rounded-lg border border-white/10 bg-secondary/40 p-3 text-left transition-colors hover:border-primary/40"
            >
              <Thumb src={fileUrl(p.photo_path)} alt={p.name} className="h-10 w-10" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{p.name}</p>
                <p className="font-num text-xs text-muted-foreground">{brl(p.price)}</p>
              </div>
              <span className="font-num text-xs text-muted-foreground">
                {Number(p.stock_units || 0).toLocaleString("pt-BR")} un
              </span>
            </button>
          ))}
        </div>
      </ResponsiveModal>
    </>
  );
}
